import { createSelector } from 'reselect'
import getLoyaltyTransferItems from '../items/getLoyaltyTransferItems'
import getExperienceItems from '../items/getExperienceItems'
import { getExperienceOneClickTransferOptions } from '../../experienceSelectors'
import { getAirportsByCode } from 'selectors/flightsSelectors'
import calculateDistance from 'lib/geo/distanceBetweenPoints'
import { getAccommodationItems } from '../hotels'

interface UnifiedTransferState {
  transferType: 'HOME_TO_AIRPORT' | 'ONE_WAY_ARRIVAL' | 'ONE_WAY_DEPARTURE' | 'FREE_RETURN_TRANSFER' | 'PAID_RETURN_TRANSFER' | null
  hasTransfers: boolean
  loyaltyTransfers: Array<App.Checkout.LoyaltyTransferItem>
  experienceTransfers: Array<App.Checkout.ExperienceItem>
  isEditing: boolean
  // Form data for HOME_TO_AIRPORT
  pickUpPlaceId?: string
  dropOffAirportCode?: string
  pickUpPlace?: App.GooglePlace
  dropOffAirport?: App.Airport
  distance?: number
  // Transfer details for experience transfers
  maxPassengers?: number
  maxCheckedBags?: number
  transferOption?: App.ExperienceTransferOption
}

const getUnifiedTransferView = createSelector(
  (state: App.State) => getLoyaltyTransferItems(state),
  (state: App.State) => getExperienceItems(state),
  (state: App.State) => getExperienceOneClickTransferOptions(state),
  (state: App.State) => state.destination.googlePlaces,
  (state: App.State) => getAirportsByCode(state),
  (state: App.State) => getAccommodationItems(state),
  (loyaltyTransfers, experienceItems, transferOptions, googlePlaces, airportsByCode): UnifiedTransferState => {
    if (loyaltyTransfers.length > 0) {
      const transfer = loyaltyTransfers[0]
      const place = googlePlaces[transfer?.pickUpLocation]
      const airport = airportsByCode[transfer?.dropOffLocation]

      return {
        transferType: 'HOME_TO_AIRPORT',
        hasTransfers: true,
        loyaltyTransfers,
        experienceTransfers: [],
        distance: (place?.latitude && place?.longitude && airport?.latitude && airport?.longitude)
          ? calculateDistance(place.latitude, place.longitude, airport.latitude, airport.longitude)
          : undefined,
        isEditing: true,
        pickUpPlaceId: transfer?.pickUpLocation,
        dropOffAirportCode: transfer?.dropOffLocation,
        pickUpPlace: place,
        dropOffAirport: airport,
      }
    }
    if (experienceItems.length > 0) {
      // Match experience items with transfer options to determine direction
      const loyaltyExperienceItems = experienceItems.filter(item => item.isLoyaltyTransfer)
      const hasToHotel = experienceItems.some(item =>
        item.isLoyaltyTransfer &&
        item.transferDirection &&
        item.transferDirection === 'AIRPORT_TO_HOTEL' &&
        item.tickets?.some(ticket => ticket.loyaltyTransferDiscount)
      )
      const hasToAirport = experienceItems.some(item =>
        item.isLoyaltyTransfer &&
        item.transferDirection &&
        item.transferDirection === 'HOTEL_TO_AIRPORT' &&
        item.tickets?.some(ticket => ticket.loyaltyTransferDiscount)
      )
      const numOfItemWithLoyaltyDiscount = loyaltyExperienceItems.filter(item =>
        item.tickets?.some(ticket => ticket.loyaltyTransferDiscount)
      )

      let transferType: UnifiedTransferState['transferType']

      if (loyaltyExperienceItems.length == 2) {
        transferType = numOfItemWithLoyaltyDiscount.length === 2 ? 'FREE_RETURN_TRANSFER' : 'PAID_RETURN_TRANSFER'
      } else if (hasToHotel) {
        transferType = 'ONE_WAY_ARRIVAL'
      } else if (hasToAirport) {
        transferType = 'ONE_WAY_DEPARTURE'
      } else {
        transferType = null
      }

      // Get transfer option details from the first experience item
      if (loyaltyExperienceItems.length > 0) {
        // Get transfer option details from the first experience item
        const firstExperienceItem = loyaltyExperienceItems[0]
        const firstTicket = firstExperienceItem.tickets?.[0]
        // Find the matching transfer option for baggage info
        let transferOption: App.ExperienceTransferOption | undefined
        if (firstTicket) {
          transferOption = [...transferOptions.toHotelOptions, ...transferOptions.toAirportOptions]
            .find(option => option.id === firstTicket.ticketId)
        }
        return {
          transferType,
          hasTransfers: true,
          loyaltyTransfers: [],
          experienceTransfers: loyaltyExperienceItems,
          isEditing: true,
          pickUpPlaceId: undefined,
          dropOffAirportCode: undefined,
          pickUpPlace: undefined,
          dropOffAirport: undefined,
          maxPassengers: transferOption?.maxPassengers,
          maxCheckedBags: transferOption?.maxCheckedBags,
          transferOption,
        }
      } else {
        // No experience items - return empty state
        return {
          transferType: null,
          hasTransfers: false,
          loyaltyTransfers: [],
          experienceTransfers: [],
          isEditing: false,
          pickUpPlaceId: undefined,
          dropOffAirportCode: undefined,
          pickUpPlace: undefined,
          dropOffAirport: undefined,
        }
      }
    }

    return {
      transferType: null,
      hasTransfers: false,
      loyaltyTransfers: [],
      experienceTransfers: [],
      isEditing: false,
      pickUpPlaceId: undefined,
      dropOffAirportCode: undefined,
      pickUpPlace: undefined,
      dropOffAirport: undefined,
    }
  }
)

export default getUnifiedTransferView
